import Axios from '../../axiosConfig';
import { BACKEND_URL } from '../../config';
import { configure, runInAction, makeAutoObservable } from 'mobx';
import {
    RegisterRequest,
    ResetPasswordRequest,
    UserDetails,
    UserDto,
    UserInfoRequest,
    UserManagementRequest,
    UserWithOrganizations,
} from '../../types/user';
import { AllOrganizationsAndUsersResponse, OrganizationDto } from '../../types/organization';
import RootModule from '../rootModule';
import EmailGroupDto from '../../types/emailGroup';

configure({ enforceActions: 'observed' });

export default class UserModule {
    private rootModule: RootModule;
    private userData: UserDetails | null = null; // Details of the signed-in user.
    private userManagementUserList: UserWithOrganizations[] = []; // Users in user management view.
    private emailGroupsByCreator: EmailGroupDto[] = [];
    private organizationUsers: UserDto[] = []; // Current organization users. Used in the current document.

    constructor(rootModule: RootModule) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.rootModule = rootModule;
    }

    public async fetchUserDetails(): Promise<void> {
        try {
            this.rootModule.loadingIndicator.showLogin();
            const response = await Axios({
                method: 'GET',
                url: BACKEND_URL + 'organizationUser/getDetails',
                withCredentials: true,
            });

            const details: UserDetails = response.data;
            details.selectedOrganization = details.homeOrganization;
            runInAction(() => (this.userData = details));
        } finally {
            this.rootModule.loadingIndicator.hideLogin();
        }
    }

    public async fetchSelectedOrganizationUserDetails(organizationId: number): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'user/getSelectedOrganizationUserDetails',
            data: { organizationId },
        });
        const details: UserDetails = response.data;
        if (!details.selectedOrganization?.name) {
            details.selectedOrganization = details.homeOrganization;
        }
        runInAction(() => (this.userData = details));
    }

    public async fetchCurrentDocumentOrganizationUsers(): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'organizationUser/organizationUsers',
            data: { id: this.rootModule.document.currentDocument.organization.id },
            withCredentials: true,
        });
        runInAction(() => (this.organizationUsers = response.data));
    }

    public get currentDocumentOrganizationUsers(): UserDto[] {
        return this.organizationUsers;
    }

    public async changeUserInfo(requestData: UserInfoRequest): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'user/changeUserInfo',
            data: requestData,
            withCredentials: true,
        });
        if (this.userData && response.status == 204) {
            const newHomeOrganization = this.userData.organizations.find(
                (x) => x.organization.id === requestData.homeOrganizationId,
            )?.organization;

            if (newHomeOrganization) runInAction(() => (this.userData!.homeOrganization = newHomeOrganization));
        }
    }

    public async changeSelectedOrganization(organizationId: number): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'user/changeSelectedOrganization',
            data: { id: organizationId },
            withCredentials: true,
        });
        if (this.userData && response.status == 204) {
            const orgUserInNewSelectedOrganization = this.userData.organizations.find(
                (orgUser) => orgUser.organization.id === organizationId,
            );

            const newSelectedOrganization = orgUserInNewSelectedOrganization?.organization;

            const authInNewSelectedOrganization = orgUserInNewSelectedOrganization?.moduleActions;
            if (newSelectedOrganization) {
                runInAction(() => {
                    this.userData!.selectedOrganization = newSelectedOrganization;
                    this.userData!.auth = authInNewSelectedOrganization || [];
                });
            }
        }
    }

    public async removeUserSelectedOrganization(): Promise<void> {
        await Axios({
            method: 'PUT',
            url: BACKEND_URL + 'user/removeSelectedOrganization',
            withCredentials: true,
        });
    }

    public async fetchAllUserManagementOrganizationsAndUsers(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'userManagement/allOrganizationsAndUsers',
            withCredentials: true,
        });
        const responseData: AllOrganizationsAndUsersResponse = response.data;

        const organizations = responseData.organizations;
        const users = responseData.users;

        runInAction(() => {
            this.rootModule.organization.setAdminOrganizationList(organizations);
            this.userManagementUserList = users;
        });
    }

    public async fetchEmailGroupsByCreator(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'emailGroup/getByCreator',
            withCredentials: true,
        });
        runInAction(() => (this.emailGroupsByCreator = response.data));
    }

    public async saveEmailGroup(emailGroup: EmailGroupDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'emailGroup/saveEmailGroup',
            data: emailGroup,
            withCredentials: true,
        });
        const savedGroup: EmailGroupDto = response.data;
        const index = this.emailGroups.findIndex((group) => group.id === savedGroup.id);
        if (index > -1) runInAction(() => (this.emailGroupsByCreator[index] = savedGroup));
        else runInAction(() => this.emailGroupsByCreator.push(savedGroup));
    }

    public async addUser(userParams: UserManagementRequest): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'userManagement/addUser',
            data: userParams,
            withCredentials: true,
        });
        runInAction(() => {
            const newUser: UserWithOrganizations = response.data;
            this.userManagementUserList.push(newUser);
        });
    }

    public async deleteUser(userId: number): Promise<void> {
        const response = await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `user/removeUser/${userId}`,
            withCredentials: true,
        });
        if (response.status === 204) {
            runInAction(
                () =>
                    (this.userManagementUserList = this.userManagementUserList.filter(
                        (userWithOrganizations) => userWithOrganizations.user.id !== userId,
                    )),
            );
        }
    }

    public async updateUser(userParams: UserManagementRequest): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'userManagement/updateUser',
            data: userParams,
            withCredentials: true,
        });
        const updatedUserWithOrganizations: UserWithOrganizations = response.data;
        const index = this.userManagementUserList.findIndex(
            (userWithOrganizations) => userWithOrganizations.user.id === updatedUserWithOrganizations.user.id,
        );
        if (index > -1) runInAction(() => (this.userManagementUserList[index] = updatedUserWithOrganizations));
    }

    public async removeEmailGroup(id: number): Promise<void> {
        const response = await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `emailGroup/removeEmailGroup/${id}`,
            withCredentials: true,
        });
        if (response.status === 204) {
            runInAction(() => {
                this.emailGroupsByCreator = this.emailGroupsByCreator.filter((group) => group.id !== id);
            });
        }
    }

    public clearUserDetails(): void {
        this.userManagementUserList = [];
        this.rootModule.organization.setAdminOrganizationList([]);
        this.userData = null;
    }

    public async resetPasswordWithToken(requestData: ResetPasswordRequest): Promise<void> {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'passwordReset/withToken',
            data: requestData,
        });
    }

    public async sendResetLink(email: string): Promise<void> {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'passwordReset/createLink',
            data: { value: email },
        });
    }

    public async register(requestData: RegisterRequest): Promise<{ code: string }> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'registration/register',
            data: requestData,
        });
        return response.data;
    }

    public get userDetails(): UserDetails | null {
        return this.userData;
    }

    public get userOrganizations(): OrganizationDto[] {
        const { locale } = this.rootModule.localization;
        const organizations = this.userDetails?.organizations || [];
        return organizations
            .slice()
            .sort((a, b) => {
                const aName =
                    a.organization.name[locale] ||
                    a.organization.name.fi_FI ||
                    a.organization.name.sv_SE ||
                    a.organization.name.en_GB ||
                    '';
                const bName =
                    b.organization.name[locale] ||
                    b.organization.name.fi_FI ||
                    b.organization.name.sv_SE ||
                    b.organization.name.en_GB ||
                    '';

                return aName > bName ? 1 : -1;
            })
            .sort((a, b) => (a.moduleActions.length > b.moduleActions.length ? -1 : 1))
            .sort((a) => (a.organization.id === this.userDetails?.homeOrganization.id ? -1 : 1))
            .map((orgUser) => orgUser.organization);
    }

    public get userManagementUsers(): UserWithOrganizations[] {
        return this.userManagementUserList;
    }

    public get isUserLoggedIn(): boolean {
        return Boolean(this.userData);
    }

    public get emailGroups(): EmailGroupDto[] {
        return this.emailGroupsByCreator;
    }

    public isAuthorized(name: string, action: string): boolean {
        if (!name || !action) return false;
        if (!this.isUserLoggedIn) return false;
        if (!Array.isArray(this.userDetails?.auth)) return false;
        const userRight = this.userDetails?.auth.filter((moduleAction) => moduleAction.module.title === name);
        if (!userRight) return false;
        return userRight.some((moduleAction) => moduleAction.title === action);
    }

    public validateUserName(userName: string): boolean {
        // Validation for basic email addresses, see: https://www.regular-expressions.info/email.html
        /*
            Current regex rules for email validation:

            - local-part can contain letters a-z, numbers 0-9, dots, -, %, + and _.

            - sub-domains can contain letters a-z, numbers 0-9 and -. Subdomain must begin with letter or number, and they
            cannot contain consecutive hyphens -. They also must end with letter or number followed by dot. Limit for
            amount of subdomains is 8.

            - top-level domain is limited to length of 63, and it must have two letters at least. Only letters a-z are allowed.
        * */
        const emailPattern = new RegExp('^[a-z0-9._%+-]+@(?:[a-z0-9]+(?:-[a-z0-9]+)*\\.){1,8}[a-z]{2,63}$');
        return emailPattern.test(userName) && userName.length < 321;
    }

    public validatePassword(password: string): boolean {
        const PASSWORD_MIN_LENGTH = 8;

        const isValidLength = password.length >= PASSWORD_MIN_LENGTH;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasNoSpaces = /\S/.test(password);

        return isValidLength && hasUpperCase && hasLowerCase && hasNumbers && hasNoSpaces;
    }

    public checkPasswordMatch(password1: string, password2: string): boolean {
        return password1 === password2;
    }
}
